import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { PlanVersion, PlanVersionSummary } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../../components/AdminPage';
import { ActionLink, AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock } from '../../components/feedback';
import { SelectField } from '../../components/fields';
import { DateTimeText, ScrollTable, StatusBadge } from '../../components/records';
import { useDialogTarget } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import { PLAN_KIND_LABELS, plural, productName, versionLabel } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { NewDraftDialog } from './NewDraftDialog';
import { SelfServeSection } from './SelfServeSection';
import { SignupsAttentionSection } from './SignupsAttentionSection';
import {
  BILLED_PRODUCTS,
  isBilledProductKey,
  isPlanVersionStatus,
  planVersionPath,
  type BilledProductKey,
  type PlanVersionStatus,
} from './shared';

/** The query string of the list: its filters. */
export function billingSearch(filters: { product?: BilledProductKey; status?: PlanVersionStatus }): string {
  const params = new URLSearchParams();
  if (filters.product) params.set('product', filters.product);
  if (filters.status) params.set('status', filters.status);
  const search = params.toString();
  return search ? `?${search}` : '';
}

/** What a page opened from here shows once: the confirmation of the change that led there. */
export interface BillingNoticeState {
  notice: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

function PlanVersionsTable({ versions, busy }: { versions: readonly PlanVersionSummary[]; busy: boolean }) {
  return (
    <ScrollTable caption="Plan versions" busy={busy} className="admin-table admin-table--plan-versions">
      <thead>
        <tr>
          <th scope="col">Plan</th>
          <th scope="col">Version</th>
          <th scope="col">Product</th>
          <th scope="col">Kind</th>
          <th scope="col">Status</th>
          <th scope="col">Published</th>
          <th scope="col">Source</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((version) => (
          <tr key={version.id}>
            <th scope="row">
              <Link to={planVersionPath(version.id)}>
                {version.planName} {versionLabel(version.version)}
              </Link>
              <span className="admin-cell-secondary">
                <code>{version.planKey}</code>
              </span>
            </th>
            <td>{versionLabel(version.version)}</td>
            <td>{productName(version.productKey)}</td>
            <td>
              {PLAN_KIND_LABELS[version.planKind] ?? version.planKind}
              {version.organizationId ? (
                <span className="admin-cell-secondary">
                  <Link to={`/organizations/${version.organizationId}/billing`}>For one organization</Link>
                </span>
              ) : null}
            </td>
            <td>
              <StatusBadge status={version.status} />
            </td>
            <td>
              {version.publishedAt ? (
                <>
                  {version.publishedBy ?? <span className="admin-muted">Unknown</span>}
                  <span className="admin-cell-secondary">
                    <DateTimeText iso={version.publishedAt} />
                  </span>
                </>
              ) : (
                <span className="admin-muted">Not yet</span>
              )}
            </td>
            <td className="admin-cell-wrap">
              {version.source || <span className="admin-muted">No source label</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

/**
 * Billing (/billing): every plan version, catalog and custom, newest first, filtered by product and status (the
 * filters live in the address). Staff with staff.billing.read see it; staff.billing.manage adds a new draft (a
 * new plan, or the next version of a plan from a copy), which opens as soon as platform-api created it. Prices
 * are read on each version's page, as platform-api sends them. It also holds the self-serve switch and the
 * sign-ups needing attention, spec 2026-10-18 piece 8.
 */
export function BillingPage() {
  const abilities = useAbilities();
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawProduct = params.get('product');
  const product = isBilledProductKey(rawProduct) ? rawProduct : undefined;
  const rawStatus = params.get('status');
  const status = isPlanVersionStatus(rawStatus) ? rawStatus : undefined;
  const filtered = Boolean(product || status);

  const key = abilities.readBilling ? `plan-versions\u0000${product ?? ''}\u0000${status ?? ''}` : null;
  const read = useAdminRead(key, (signal) =>
    api.listPlanVersions(
      { ...(product ? { productKey: product } : {}), ...(status ? { status } : {}) },
      signal,
    ),
  );
  const current = read.dataKey === key && !read.failure ? read.data : null;
  const shown = read.failure ? null : read.data;
  const dialog = useDialogTarget<'new'>();

  const go = (filters: { product?: BilledProductKey; status?: PlanVersionStatus }) =>
    void navigate({ search: billingSearch(filters) }, { replace: true });

  // The dialog needs every version to choose a plan or a copy from, not only the filtered ones: a filtered list
  // reads them all once the dialog opens; an unfiltered list already has them.
  const allKey = abilities.manageBilling && dialog.open && filtered ? 'plan-versions\u0000all' : null;
  const allRead = useAdminRead(allKey, (signal) => api.listPlanVersions({}, signal));
  const allVersions = filtered
    ? allRead.dataKey === allKey && !allRead.failure
      ? (allRead.data?.items ?? null)
      : null
    : (current?.items ?? null);

  let statusText = '';
  if (read.loading) statusText = 'Loading plan versions';
  else if (current) {
    const count = plural(current.items.length, 'plan version', 'plan versions');
    statusText = filtered ? `${count} match` : count;
  }

  let results: ReactNode;
  if (!abilities.readBilling) {
    results = <AdminProblem failure={{ kind: 'forbidden' }} subject="billing" />;
  } else if (read.failure) {
    results = <AdminProblem failure={read.failure} subject="the plan versions" onRetry={read.retry} />;
  } else if (shown && shown.items.length > 0) {
    results = <PlanVersionsTable versions={shown.items} busy={read.loading} />;
  } else if (current) {
    results = filtered ? (
      <EmptyPageState
        icon={<NavigationGlyph name="Search" />}
        title="No plan version matches"
        description="The filters narrow by product and status."
        action={<ActionLink to={{ search: '' }}>Clear the filters</ActionLink>}
      />
    ) : (
      <EmptyPageState
        icon={<NavigationGlyph name="WalletCards" />}
        title="No plan versions yet"
        description="Nothing is priced until a plan version is drafted and published."
      />
    );
  } else {
    results = <LoadingBlock label="Loading the plan versions" />;
  }

  const productOptions = [
    { value: '', label: 'Any product' },
    ...BILLED_PRODUCTS.map((key) => ({ value: key, label: productName(key) })),
  ];

  return (
    <AdminPage title="Billing">
      <PageHeading
        title="Billing"
        description="Plan versions price what Trace sells: catalog plans for anyone, custom plans for one organization's deal. Each organization's contracts, usage and invoices are on its Billing section."
        actions={
          abilities.readBilling && abilities.manageBilling ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => dialog.show('new')}
            >
              New draft
            </button>
          ) : undefined
        }
      />
      {abilities.readBilling ? (
        <>
          <SelfServeSection />
          <SignupsAttentionSection />
        </>
      ) : null}
      <PageSection
        title="Plan versions"
        description="A draft is edited until it is published; a published version never changes, and only published versions can be signed."
      >
        {abilities.readBilling ? (
          <div className="admin-toolbar">
            <div className="admin-filters">
              <SelectField
                label="Product"
                value={product ?? ''}
                options={productOptions}
                onChange={(value) => go({ product: isBilledProductKey(value) ? value : undefined, status })}
              />
              <SelectField
                label="Status"
                value={status ?? ''}
                options={STATUS_OPTIONS}
                onChange={(value) => go({ product, status: isPlanVersionStatus(value) ? value : undefined })}
              />
            </div>
            <p className="admin-results-status" role="status" aria-live="polite">
              {statusText}
            </p>
          </div>
        ) : null}
        {results}
      </PageSection>
      {abilities.manageBilling ? (
        <NewDraftDialog
          key={dialog.key}
          open={dialog.open}
          onClose={dialog.close}
          versions={allVersions}
          onCreated={(version: PlanVersion) => {
            const state: BillingNoticeState = {
              notice: `Draft ${versionLabel(version.version)} of ${version.planName} created.`,
            };
            void navigate(planVersionPath(version.id), { state });
          }}
        />
      ) : null}
    </AdminPage>
  );
}
